import { useEffect, useRef, useCallback } from 'react'
import { useChats } from '../../hooks/useChats'
import ChatItem from './ChatItem'

export default function ChatList({ selectedChatId, onSelectChat }) {
  const { chats, loading, loadingMore, error, pagination, loadMore } = useChats()
  const observerRef = useRef(null)
  const loadMoreTriggerRef = useRef(null)
  const lastLoadTimeRef = useRef(0)

  // Infinite scroll con IntersectionObserver + debounce simple
  const handleObserver = useCallback((entries) => {
    const [entry] = entries
    const now = Date.now()
    const timeSinceLastLoad = now - lastLoadTimeRef.current

    // Solo cargar si: está visible, hay más, no está cargando, y pasó al menos 500ms
    if (entry.isIntersecting && pagination.hasMore && !loadingMore && timeSinceLastLoad > 500) {
      lastLoadTimeRef.current = now
      loadMore()
    }
  }, [pagination.hasMore, loadingMore, loadMore])

  useEffect(() => {
    const element = loadMoreTriggerRef.current
    if (!element) return

    observerRef.current = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: '100px',
      threshold: 0
    })

    observerRef.current.observe(element)

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
    }
  }, [handleObserver])

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
          <p className="text-sm text-gray-500">Cargando chats...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center p-4 bg-gray-100">
        <div className="text-center">
          <p className="text-sm text-red-600 mb-2">Error al cargar chats</p>
          <p className="text-xs text-gray-500">{error}</p>
        </div>
      </div>
    )
  }

  if (chats.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center p-4 bg-gray-100">
        <p className="text-sm text-gray-500">No hay chats disponibles</p>
      </div>
    )
  }

  return (
    <div className="flex-1 overflow-y-auto bg-gray-100 p-3 space-y-2">
      {chats.map(chat => (
        <ChatItem
          key={chat.id}
          chat={chat}
          isSelected={selectedChatId === chat.id}
          onClick={() => onSelectChat(chat)}
        />
      ))}

      {/* Trigger para infinite scroll */}
      <div ref={loadMoreTriggerRef} className="h-10" />

      {/* Indicador de carga */}
      {loadingMore && (
        <div className="flex items-center justify-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
          <span className="ml-2 text-sm text-gray-500">Cargando más chats...</span>
        </div>
      )}

      {/* Indicador de total cargado */}
      {!pagination.hasMore && chats.length > 0 && (
        <div className="pt-2 pb-4 text-center text-sm text-gray-500">
          {chats.length} chats cargados
        </div>
      )}
    </div>
  )
}
